"use client";

import { useState } from "react";
import { useTranslation } from "react-i18next";
import VerifyClinicModal from "./VerifyClinicModal";
import RejectClinicModal from "./RejectClinicModal";
import "./ClinicReviewCard.css";

const ClinicReviewCard = ({ clinic, onViewDetails, onVerify, onReject, onUpgradeSubscription, onDowngradeSubscription }) => {
  const { t } = useTranslation();
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);

  const isUnverified = clinic.status === 'unverified';
  const isVerified = clinic.status === 'verified';
  const isRejected = clinic.status === 'rejected';
  
  // Check subscription plan
  const subscriptionPlan = 
    clinic.clinic_subscription?.plan ||
    clinic.clinic_subscription_plan;
  const isPaid = subscriptionPlan === 'paid';
  
  // Check if clinic has requested upgrade (only show upgrade button if they have)
  const hasUpgradeRequest = 
    clinic.clinic_subscription?.upgrade_requested === true ||
    clinic.clinic_subscription?.has_upgrade_request === true ||
    clinic.upgrade_requested === true ||
    clinic.has_upgrade_request === true;

  const handleVerify = (e) => {
    e.stopPropagation();
    setShowVerifyModal(true);
  };

  const handleReject = (e) => {
    e.stopPropagation();
    setShowRejectModal(true);
  };

  const handleVerifyConfirm = () => {
    onVerify(clinic.id);
  };

  const handleRejectConfirm = (reason) => {
    onReject(clinic.id, reason);
  };

  return (
    <div 
      className={`clinic-review-card ${isUnverified ? 'unverified' : isVerified ? 'verified' : isRejected ? 'rejected' : 'unverified'}`}
      onClick={() => onViewDetails(clinic)}
    >
      <div className="clinic-review-card-header">
        <div className="clinic-review-card-status">
          {isUnverified && (
            <span className="status-badge status-unverified">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
              {t("adminDashboard.card.status.unverified")}
            </span>
          )}
          {isVerified && (
            <span className="status-badge status-verified">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22 4 12 14.01 9 11.01"></polyline>
              </svg>
              {t("adminDashboard.card.status.verified")}
            </span>
          )}
          {isRejected && (
            <span className="status-badge status-rejected">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="15" y1="9" x2="9" y2="15"></line>
                <line x1="9" y1="9" x2="15" y2="15"></line>
              </svg>
              {t("adminDashboard.card.status.rejected")}
            </span>
          )}
        </div>
        <div className="clinic-review-card-date">
          {(() => {
            try {
              const date = new Date(clinic.created_at);
              if (isNaN(date.getTime())) {
                return t("adminDashboard.card.invalidDate");
              }
              return date.toLocaleDateString();
            } catch (error) {
              return t("adminDashboard.card.invalidDate");
            }
          })()}
        </div>
      </div>

      <div className="clinic-review-card-content">
        <h3 className="clinic-review-card-name">
          {clinic.name_en || clinic.name_ar}
          {clinic.name_ar && clinic.name_en && (
            <span className="clinic-review-card-name-ar"> ({clinic.name_ar})</span>
          )}
        </h3>
        
        <div className="clinic-review-card-info">
          <div className="clinic-review-card-info-item">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
              <circle cx="12" cy="10" r="3"></circle>
            </svg>
            <span>
              {typeof clinic.city === 'object' 
                ? (clinic.city.name_en || clinic.city.name_ar || clinic.city.name || t("adminDashboard.card.notAvailable"))
                : (clinic.city_name || clinic.city || t("adminDashboard.card.notAvailable"))}
            </span>
          </div>
          <div className="clinic-review-card-info-item">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
              <polyline points="22,6 12,13 2,6"></polyline>
            </svg>
            <span>{clinic.email || t("adminDashboard.card.notAvailable")}</span>
          </div>
          <div className="clinic-review-card-info-item">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
              <path d="M22 6l-10 7L2 6"></path>
            </svg>
            <span>{clinic.registration_number || t("adminDashboard.card.notAvailable")}</span>
          </div>
          {(() => {
            const owner = clinic.owner || clinic.user;
            if (!owner) return null;
            
            const ownerName = owner.full_name || `${owner.first_name || ''} ${owner.last_name || ''}`.trim() || owner.name || owner.username || null;
            if (!ownerName) return null;
            
            return (
              <div className="clinic-review-card-info-item">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
                <span>
                  <strong>{t("adminDashboard.card.owner")}:</strong> {ownerName}
                </span>
              </div>
            );
          })()}
        </div>
      </div>

      {isUnverified && (
        <div className="clinic-review-card-actions" onClick={(e) => e.stopPropagation()}>
          <button
            className="clinic-review-card-btn clinic-review-card-btn-verify"
            onClick={handleVerify}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
              <polyline points="22 4 12 14.01 9 11.01"></polyline>
            </svg>
            {t("adminDashboard.card.actions.verify")}
          </button>
          <button
            className="clinic-review-card-btn clinic-review-card-btn-reject"
            onClick={handleReject}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="15" y1="9" x2="9" y2="15"></line>
              <line x1="9" y1="9" x2="15" y2="15"></line>
            </svg>
            {t("adminDashboard.card.actions.reject")}
          </button>
          <button
            className="clinic-review-card-btn clinic-review-card-btn-view"
            onClick={() => onViewDetails(clinic)}
          >
            {t("adminDashboard.card.actions.viewDetails")}
          </button>
        </div>
      )}

      {isVerified && (
        <div className="clinic-review-card-actions" onClick={(e) => e.stopPropagation()}>
          {!isPaid && hasUpgradeRequest ? (
            <button
              className="clinic-review-card-btn clinic-review-card-btn-upgrade"
              onClick={(e) => {
                e.stopPropagation();
                onUpgradeSubscription(clinic.id);
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
              </svg>
              {t("adminDashboard.card.actions.upgradeToPaid")}
            </button>
          ) : isPaid ? (
            <button
              className="clinic-review-card-btn clinic-review-card-btn-downgrade"
              onClick={(e) => {
                e.stopPropagation();
                onDowngradeSubscription(clinic.id);
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 22v-20M7 19H14.5a3.5 3.5 0 0 0 0-7h-5a3.5 3.5 0 0 1 0-7H18" />
              </svg>
              {t("adminDashboard.card.actions.downgradeToFree")}
            </button>
          ) : null}
          <button
            className="clinic-review-card-btn clinic-review-card-btn-view"
            onClick={() => onViewDetails(clinic)}
          >
            {t("adminDashboard.card.actions.viewDetails")}
          </button>
        </div>
      )}

      {isRejected && (
        <div className="clinic-review-card-actions" onClick={(e) => e.stopPropagation()}>
          <button
            className="clinic-review-card-btn clinic-review-card-btn-verify"
            onClick={handleVerify}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
              <polyline points="22 4 12 14.01 9 11.01"></polyline>
            </svg>
            {t("adminDashboard.card.actions.verify")}
          </button>
          <button
            className="clinic-review-card-btn clinic-review-card-btn-view"
            onClick={() => onViewDetails(clinic)}
          >
            {t("adminDashboard.card.actions.viewDetails")}
          </button>
        </div>
      )}

      {showVerifyModal && (
        <VerifyClinicModal
          isOpen={showVerifyModal}
          onClose={() => setShowVerifyModal(false)}
          clinic={clinic}
          onConfirm={handleVerifyConfirm}
        />
      )}

      {showRejectModal && (
        <RejectClinicModal
          isOpen={showRejectModal}
          onClose={() => setShowRejectModal(false)}
          clinic={clinic}
          onConfirm={handleRejectConfirm}
        />
      )}
    </div>
  );
};

export default ClinicReviewCard;

